import type { ViewContainerRef } from '@angular/core';
import type { UrlTree } from '@angular/router';

import { Observable } from 'rxjs';

import type { User } from '@core/account-types';
import type { BillingEstimate } from '@core/billing-estimate-types';
import type { BillingAddressFormFields, BillingInformation } from '@core/billing-information-types';
import type { ChargebeeEntity } from '@core/company-types';
import type { InvoiceResourceType, InvoiceType } from '@core/invoices-types';
import type { Currency } from '@core/pricing-types';
import type { BillingPlan } from '@core/products-types';
import { BillingPlanTier } from '@core/products-types';
import type { CompanyVerificationRequestStatus } from '@core/spam-verification-data';
import type {
  Addon,
  CancelPayload,
  PaymentIntent,
  PaymentInterval,
  PricingFeatureSubscription,
} from '@core/subscription-types';

import type { StripeElements } from './stripe.interfaces';
import type { NextPaymentInfoUI } from '../pages/billing-page/billing-page.types';

export const TrialProductCode = (pp24Enabled?: boolean) =>
  pp24Enabled ? BillingPlanTier.Grow : BillingPlanTier.Scale;
export type TrialProductCode = typeof BillingPlanTier.Scale | BillingPlanTier.Grow;

export enum PaymentPeriods {
  Annually,
  Monthly,
}

export abstract class PricingFeatureFacade {
  public abstract orderCompletedSuccessfully(
    hasSampleData: boolean,
    showPlanChangeMessage?: boolean,
  ): void;

  public abstract getInvoices(): Observable<InvoiceType[]>;
  public abstract loadMoreInvoices(): void;
  public abstract downloadInvoice(invoiceId: string, resourceType: InvoiceResourceType): void;
  public abstract getNextPaymentInfo(): Observable<NextPaymentInfoUI | null>;
  public abstract getBillingPlans(): Observable<BillingPlan[]>;
  public abstract getActiveBillingPlan(): Observable<BillingPlan | undefined>;
  public abstract getBillingPlansWithActive(): Observable<BillingPlan[]>;
  public abstract getBillingPlanById(id: number): Observable<BillingPlan>;
  public abstract getAdminsWithManageSubscriptionAbility(): Observable<User[]>;
  public abstract fetchPlansIfNeeded(): void;

  public abstract getLimitedAccessUrl(): UrlTree;
  public abstract getEndedTrialUrl(): UrlTree;
  public abstract getBillingPageUrl(): UrlTree;
  public abstract getActiveTrialUrl(): UrlTree;
  public abstract getSubscriptionExpiredUrl(): UrlTree;
  public abstract getSubscriptionBlockedUrl(): UrlTree;
  public abstract getSubscriptionChurnPageUrl(): UrlTree;
  public abstract getPlanPickerUrl(config?: { legacyPlans: boolean }): UrlTree;
  public abstract getBillingPageUrlFromModel(model: PricingFeatureLoadedModel): UrlTree | null;
  public abstract hasSampleData(): Observable<boolean>;

  public abstract openPlanPicker(config?: ProductOrderConfig): Promise<boolean>;
  public abstract openBilling(
    productId: number,
    slots: number,
    interval?: PaymentInterval,
    downgradeScreenViewed?: boolean,
  ): Promise<boolean>;
  public abstract openBilling2024(
    productId: number,
    quantity?: number,
    interval?: PaymentInterval,
    downgradeScreenViewed?: boolean,
  ): Promise<boolean>;

  public abstract cancelSubscription(data: CancelPayload): Observable<boolean>;

  public abstract changeAddons(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    addons: Addon[],
    skipPaymentIntent: boolean,
  ): Observable<boolean>;

  public abstract newChangeSlots(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    slotsIncreased: boolean,
    addons: Addon[],
    isWithPaymentIntent: boolean,
  ): Observable<boolean>;

  public abstract updatePaymentMethodData(
    data: PaymentMethodInfo,
    elements: StripeElements,
    genericErrorHandler?: boolean,
  ): Observable<boolean>;

  public abstract updateBillingData(data: Partial<BillingInformation>): Observable<boolean>;
  public abstract updateBillingContactData(data: BillingContactFormFields): Observable<boolean>;
  public abstract updateBillingAddressData(data: BillingAddressFormFields): Observable<boolean>;
  public abstract deleteCreditCard(): Observable<boolean>;

  public abstract changeSlots(slots: number, currentSlots: number): Observable<boolean>;
  public abstract reactivatePlan(): Observable<boolean>;
  public abstract changePlan(
    productId: number,
    slots: number,
    paymentInterval: PaymentInterval,
  ): Observable<boolean>;

  public abstract openSlotsPickerDialog(
    product: BillingPlan,
    model: PricingFeatureLoadedModel,
    manual?: boolean,
    vcRef?: ViewContainerRef,
  ): void;

  public abstract openIntercom(): void;

  public abstract paymentIntent(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    addons: Addon[],
    sizeRange?: string,
  ): Observable<PaymentIntent | null>;

  public abstract patchSubscription(
    billingPlanId: number,
    billingPeriod: 1 | 12,
    addons: Addon[],
    paymentIntentId?: string | null,
    fteRange?: string,
  ): Observable<any>;

  public abstract changeCurrency(
    currency: Currency,
    product: BillingPlan,
    model: PricingFeatureLoadedModel,
    interval: PaymentInterval,
    slots?: number,
    quantity?: number,
  ): void;

  public abstract loadBillingEstimate(
    billingPeriod: PaymentInterval,
    billingPlanId: number,
    addons: Addon[],
    sizeRange?: string,
  ): Observable<BillingEstimate>;
}

export abstract class PricingFeatureObservableModel extends Observable<PricingFeatureModel> {}

export type PricingFeatureModel = PricingFeatureInitialModel | PricingFeatureLoadedModel;

export interface Trial {
  productCode: TrialProductCode;
  untilDate?: string;
  expired?: boolean;
}

export interface PricingFeatureInitialModel {
  loaded: false;
  loading: boolean;
}

export interface PricingFeatureLoadedModel {
  loading: boolean;
  loaded: true;
  currency: Currency;
  currencyLock: boolean;
  countryCode: string;
  chargebeeEntity: ChargebeeEntity;
  subscription?: PricingFeatureSubscription;
  subscriptionStatus: {
    expired: boolean;
    blocked: boolean;
    canceled: boolean;
    pastDue: boolean;
    paused: boolean;
    future: boolean;
    inactive: boolean;
    trialing: boolean;
    nonRenewing: boolean;
  };
  trial?: Trial;
  externalBilling: boolean;
  billingInformation?: Partial<BillingInformation>;
  slotsTotal: number;
  jobSlotsUsed: number;
  canManageSubscription: boolean;
  hasSampleData: boolean;
  changeCurrency: (currency: Currency) => void;
  spamVerificationStatus: CompanyVerificationRequestStatus;
  isPP24: boolean;
  isAgency: boolean;
}

export type ProductOrderConfig = {
  skipRedirectOnSuccess?: boolean;
  legacyPlans?: boolean;
};

export type BillingContactFormFields = Pick<
  BillingInformation,
  'fullName' | 'email' | 'phone' | 'invoiceEmails' | 'primaryContactId'
>;

export type PaymentMethodInfo = Pick<
  BillingInformation,
  'stripeToken' | 'stripeCardLast4' | 'stripeCardExpiration' | 'stripeCardType' | 'validCreditCard'
>;
